import React, { useMemo, useState } from "react";
import Grid from "@material-ui/core/Grid";
import { PrimaryButton, SecondaryButton } from "@prestosports/commons-ui";
import Divider from "@material-ui/core/Divider";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Toolbar from "@material-ui/core/Toolbar";
import * as Yup from "yup";
import { useFormik } from "formik";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Box from "@material-ui/core/Box";
import FormHelperText from "@material-ui/core/FormHelperText";
import gameFinalizeStyles from "../styles/GameFinalize.styles";
import commonStyles from "../../common/styles/Common.styles";
import ClockTimePicker from "../../common/components/ClockTimePicker";
import MatchSummaryList from "./MatchSummaryList";
import ScoreBoardContainer from "../../game-stats/containers/ScoreBoardContainer";
import { useAppContext } from "../../../state/app/context";
import DurationPicker from "../../common/components/DurationPicker";
import { useGameContextContext } from "../../../state/game-context/context";

const GameFinalize = ({
	singles,
	doubles,
	settings,
	event,
	onFinalize,
	onSave,
	onCancel,
	onChangeMatchesOrder
}) => {
	const { state: appState } = useAppContext();
	const isSubmitting = appState.isSubmitting();

	const [dialogOpen, setDialogOpen] = useState(false);

	const { state: gameContextState } = useGameContextContext();
	const { finalizeOrder } = gameContextState.finalizeSettings();
	const { singles: finalizedSingles, doubles: finalizedDoubles } = finalizeOrder;
	const allMatchesFinalized = finalizedSingles.length || finalizedDoubles.length;

	const classes = gameFinalizeStyles();
	const commonClasses = commonStyles();

	const durationError = "Duration is required";
	const defaultDuration = "00:00:00";
	const defaultEndTime = useMemo(() => new Date().toISOString(), []);
	const validationSchema = Yup.object({
		duration: Yup.string().notOneOf([defaultDuration], durationError),
		attendance: Yup.number().min(
			0,
			"Attendance must be greater than or equal to zero"
		)
	});

	const formik = useFormik({
		enableReinitialize: false,
		initialErrors: settings.duration ? {} : { duration: durationError },
		initialValues: {
			attendance: "",
			notes: event.notes,
			endTime: defaultEndTime,
			duration: defaultDuration,
			...settings
		},
		validationSchema,
		onSubmit: (values) => {
			onFinalize(values);
		}
	});

	const { values, touched, handleSubmit, handleChange, errors, setFieldValue } =
		formik;

	const saveDisabled = isSubmitting || Object.keys(errors).length;

	const setEndTime = (value) => {
		setFieldValue("endTime", value);
	};

	const setDuration = (value) => {
		setFieldValue("duration", value);
	};

	const changeMatchesOrderHandler = (isSingles, newOrder) => {
		onChangeMatchesOrder(isSingles, newOrder);
	};

	const handleCancel = () => {
		if (!isSubmitting) onCancel();
	};

	const handleDialogClose = () => {
		setDialogOpen(false);
	};

	const handleSave = () => {
		onSave(values);
	};

	return (
		<form onSubmit={handleSubmit} className={commonClasses.form}>
			<Grid container spacing={2} className={classes.container}>
				<Grid item sm={12} md={12}>
					<ScoreBoardContainer />
				</Grid>
				<Grid item sm={12} md={12}>
					<Typography variant="h5">Event Wrap-Up</Typography>
				</Grid>
				<Grid item sm={2} md={2}>
					<TextField
						variant="outlined"
						id="attendance"
						name="attendance"
						label="Attendance"
						value={values.attendance}
						error={touched.attendance && Boolean(errors.attendance)}
						helperText={touched.attendance && errors.attendance}
						onChange={handleChange}
						className={classes.attendance}
						type="number"
					/>
				</Grid>
				<Grid item sm={2} md={2} style={{ marginTop: 2 }}>
					<ClockTimePicker
						value={values.endTime}
						handleTimeChange={setEndTime}
						label="End Time"
					/>
				</Grid>
				<Grid item sm={2}>
					<DurationPicker
						label="Duration"
						handleDurationChange={setDuration}
						value={values.duration}
					/>
				</Grid>
				<Grid item xs={12}>
					<Box className={classes.errorBox}>
						{Object.values(errors).map((error) => (
							<FormHelperText error={Boolean(error)}>{error}</FormHelperText>
						))}
					</Box>
				</Grid>

				{!!singles.length && (
					<>
						<Grid item sm={12} md={12}>
							<Typography variant="h5">Singles - Order of Finish</Typography>
							<MatchSummaryList
								order={settings.finalizeOrder?.singles}
								matches={singles}
								matchType="Single"
								onChangeMatchesOrder={(newOrder) =>
									changeMatchesOrderHandler(true, newOrder)
								}
							/>
						</Grid>
						<Divider className={commonClasses.divider} />
					</>
				)}

				{!!doubles.length && (
					<>
						<Grid item sm={12} md={12}>
							<Typography variant="h5">Doubles - Order of Finish</Typography>
							<MatchSummaryList
								order={settings.finalizeOrder?.doubles}
								matches={doubles}
								matchType="Double"
								onChangeMatchesOrder={(newOrder) =>
									changeMatchesOrderHandler(false, newOrder)
								}
							/>
						</Grid>
						<Divider className={commonClasses.divider} />
					</>
				)}

				<Grid item sm={12} md={12}>
					<Typography variant="h5">Notes</Typography>
				</Grid>
				<Grid item sm={12} md={12}>
					<TextField
						fullWidth
						variant="outlined"
						placeholder="Leave a note..."
						id="notes"
						name="notes"
						label="Notes"
						multiline
						rows={2}
						value={values.notes}
						onChange={handleChange}
					/>
				</Grid>
			</Grid>

			<Toolbar className={commonClasses.actionToolbar}>
				<Grid container justifyContent="flex-end" spacing={1}>
					<Grid item>
						<SecondaryButton onClick={handleCancel} label="Cancel" />
					</Grid>
					<Grid item>
						<SecondaryButton
							id="button-save"
							onClick={handleSave}
							label="Save"
							disabled={saveDisabled}
						/>
					</Grid>
					<Grid item>
						<PrimaryButton
							id="button-finalize"
							label="Finalize"
							onClick={allMatchesFinalized ? handleSubmit : () => setDialogOpen(true)}
							disabled={saveDisabled}
						/>
					</Grid>
				</Grid>
			</Toolbar>
			<Dialog open={dialogOpen} onClose={handleDialogClose}>
				<DialogTitle>Missing Matches</DialogTitle>
				<DialogContent>
					Either one singles or doubles match need to be created before finalizing
					the event.
				</DialogContent>
				<DialogActions>
					<SecondaryButton onClick={handleDialogClose} label="Cancel" />
					<PrimaryButton
						onClick={handleSubmit}
						id="unfinished-matches-save"
						label="Continue"
						disabled={isSubmitting}
					/>
				</DialogActions>
			</Dialog>
		</form>
	);
};

export default GameFinalize;
